import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fromJS } from "immutable";
import {
  PencilIcon,
  PlusIcon,
  TagIcon,
  ChevronDownIcon,
  CheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { ColorPicker } from "./ColorPicker";
import InputInline from "../../../components/InputInline";
import PrimaryButton from "../../../components/PrimaryButton";
import SecondaryButton from "../../../components/SecondaryButton";
import { deleteLabel, saveLabel } from "../../../api/LabelApi";
import LabelAction from "../../../actions/LabelAction";
import { Radio, RadioGroup } from "@headlessui/react";
import DropdownLayout from "../../../components/DropdownLayout";

const LabelPicker = (props: any) => {
  const {
    labels = fromJS([]),
    onChange,
    border = false,
    colorMode = false,
    type = "SALE",
    projectId,
    renderActivator,
    renderAssigned,
    renderList,
    renderForm,
    maxWidth,
  } = props;
  const dispatch = useDispatch();
  const allLabels = useSelector((state: any) => state.labelReducer.get(type));

  const [assignedLabels, setAssignedLabels] = useState<any[]>([]);
  const [labelList, setLabelList] = useState<any[]>([]);
  const [showLabelForm, setShowLabelForm] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<any>({
    color: "",
    name: "",
  });

  useEffect(() => {
    if (!allLabels) return;
    if (labels && labels.size > 0) {
      const filteredLabels = allLabels.filter((item: any) => labels.includes(item.get("id")));
      setAssignedLabels(filteredLabels.toJS());
    } else {
      setAssignedLabels([]);
    }
    setLabelList(allLabels.toJS());
  }, [labels, allLabels]);

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers used in useMemo
  const selectLabel = (label: any) => {
    const exists = assignedLabels.some((l) => l.id === label.id);
    if (exists) return;
    const next = [...assignedLabels, label];
    setAssignedLabels(next);
    onChange(next.map((l) => l.id).join(","));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const removeLabel = (labelId: string) => {
    const next = assignedLabels.filter((l) => l.id !== labelId);
    setAssignedLabels(next);
    onChange(next.map((l) => l.id).join(","));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const searchLabels = (value?: string) => {
    if (!value) {
      setLabelList(allLabels?.toJS?.() ?? []);
      return;
    }
    const items = allLabels.filter((item: any) => (item.get("name") || "").includes(value)).toJS();
    setLabelList(items);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const refreshLabels = () => {
    dispatch(LabelAction.getLabels(projectId, type));
  };

  const renderSearchedLabels = () => {
    return (
      <div className={"space-y-1 pt-2 max-h-56 overflow-y-auto pr-1"}>
        {labelList.map((label: any) => {
          const { id, name, color } = label;
          const isSelected = assignedLabels.some((l) => l.id === id);
          return (
            <div
              key={`search_${id}`}
              className={
                "w-full group flex items-center justify-between rounded-md hover:bg-blue-50 px-1 py-0.5 cursor-pointer"
              }
              onClick={() => {
                if (isSelected) {
                  removeLabel(id);
                } else {
                  selectLabel(label);
                }
              }}
            >
              <span
                className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${color} ring-1 ring-inset ring-gray-500/10`}
              >
                {name}
              </span>
              <div className={"flex items-center gap-2 pl-2"}>
                <button
                  type="button"
                  title="Edit"
                  className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 hover:bg-blue-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowLabelForm(true);
                    setSelectedLabel(label);
                  }}
                >
                  <PencilIcon className="h-4 w-4 text-blue-600" />
                </button>
                {isSelected && <CheckIcon className="h-4 w-4 text-blue-600" />}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderLabels = () => {
    return (
      <div className={"justify-start flex flex-wrap gap-x-1 gap-y-1"}>
        {assignedLabels.map((item: any) => (
          <span
            key={`label_${item.id}`}
            onClick={() => {
              selectLabel(item);
            }}
            className={`max-w-32 inline-flex items-center gap-x-0.5 rounded-md bg-gray-50 px-2 py-1 text-xs font-medium ${item.color} ring-1 ring-inset ring-gray-500/10`}
          >
            {item.name}
            <button
              type="button"
              onClick={() => {
                removeLabel(item.id);
              }}
              className="group relative -mr-1 h-3.5 w-3.5 rounded-xs hover:bg-gray-500/20"
            >
              <span className="sr-only">Remove</span>
              <svg
                viewBox="0 0 14 14"
                className="h-3.5 w-3.5 stroke-gray-600/50 group-hover:stroke-gray-600/75"
              >
                <path d="M4 4l6 6m0-6l-6 6" />
              </svg>
              <span className="absolute -inset-1" />
            </button>
          </span>
        ))}
      </div>
    );
  };

  const renderPlaceholder = () => {
    if (assignedLabels && assignedLabels.length > 0) {
      return renderLabels();
    }
    return (
      <div className={"flex space-x-2 items-center text-gray-600"}>
        {<TagIcon className={"w-4"} />}
        <span>Add labels</span>
      </div>
    );
  };
  const borderCSS = border ? "border border-gray-300" : "";
  const widthStyle = maxWidth ? { maxWidth } : {};
  const ctx = useMemo(
    () => ({
      assignedLabels,
      labelList,
      showLabelForm,
      selectedLabel,
      setShowLabelForm,
      setSelectedLabel,
      selectLabel,
      removeLabel,
      searchLabels,
      refreshLabels,
      type,
      projectId,
    }),
    [
      assignedLabels,
      labelList,
      showLabelForm,
      selectedLabel,
      selectLabel,
      removeLabel,
      searchLabels,
      refreshLabels,
      type,
      projectId,
    ]
  );
  const activator = renderActivator ? (
    renderActivator(ctx)
  ) : (
    <div
      className={`btn-show-list-user w-full cursor-pointer flex ${borderCSS} rounded-md bg-white px-3 py-2 text-sm items-center justify-between text-left`}
      style={widthStyle}
    >
      <div className={"flex-1 min-w-0 whitespace-normal break-words"}>{renderPlaceholder()}</div>
      <ChevronDownIcon className={"w-4 h-4 text-gray-500"} />
    </div>
  );

  const isValidForm = (selectedLabel as any).name != "" && (selectedLabel as any).color != "";

  const renderLabelForm = () => {
    if (!showLabelForm) {
      return null;
    }
    if (renderForm) {
      return renderForm(ctx);
    }
    const isEditing = (selectedLabel as any).id && (selectedLabel as any).id != "";
    return (
      <div className={"w-full space-y-3"}>
        <div
          className={
            "text-sm font-semibold text-gray-900 pb-2 border-b border-gray-300 bg-gray-100 -mx-2 -mt-2 px-2 pt-2 rounded-t"
          }
        >
          {isEditing ? "Edit label" : "Add label"}
        </div>
        <div className={"space-y-2"}>
          <div className={"text-sm text-gray-700"}>Label name</div>
          <div className={"w-full space-x-1 flex items-center"}>
            <InputInline
              border={true}
              placeholder={"e.g Marketing"}
              value={(selectedLabel as any).name}
              onFinish={(newLabelName) => {
                setSelectedLabel({
                  ...(selectedLabel as any),
                  name: newLabelName,
                });
              }}
            />
            {isValidForm && <div className={"text-red-500"}>*</div>}
          </div>
        </div>

        <div className={"space-y-2"}>
          <div className={"text-sm text-gray-700"}>Label color</div>
          <ColorPicker
            color={(selectedLabel as any).color}
            onSelectItem={(item) => {
              setSelectedLabel({
                ...(selectedLabel as any),
                color: item.color,
              });
            }}
          />
        </div>

        <div
          className={
            "mt-2 border-t border-gray-300 bg-gray-100 -mx-2 py-1 w-full flex items-center justify-between rounded-b"
          }
        >
          {isEditing ? (
            <button
              type="button"
              className={"rounded-md px-2 py-1 text-gray-600 hover:bg-gray-100"}
              onClick={() => {
                deleteLabel(projectId, (selectedLabel as any).id).then(() => {
                  refreshLabels();
                  setShowLabelForm(false);
                });
              }}
              title={"Delete"}
            >
              <TrashIcon className={"w-5 h-5"} />
            </button>
          ) : (
            <div />
          )}

          <div className={"flex items-center space-x-2"}>
            <SecondaryButton
              label={"Cancel"}
              className={"px-2 py-1 text-sm rounded-md"}
              onClick={() => {
                setShowLabelForm(false);
              }}
            />
            <PrimaryButton
              label={"Save"}
              disabled={!isValidForm}
              className={"px-2 py-1 text-sm rounded-md bg-green-600 hover:bg-green-500"}
              onClick={() => {
                if (isValidForm) {
                  saveLabel(projectId, {
                    ...(selectedLabel as any),
                    type,
                  }).then(() => {
                    refreshLabels();
                    setShowLabelForm(false);
                  });
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  const renderLabelList = () => {
    if (showLabelForm) {
      return null;
    }
    if (renderList) {
      return renderList(ctx);
    }
    return (
      <div className={"min-w-[220px]"}>
        <div className={"w-full mb-2"}>
          <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-gray-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
            <input
              className="w-full rounded-md border border-gray-300 pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Search for labels"
              onChange={(e) => searchLabels(e.target.value)}
            />
          </div>
        </div>
        {renderSearchedLabels()}
        <div
          className="border-t border-gray-100 mt-2 w-full text-blue-600 text-sm cursor-pointer select-none inline-flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded"
          onClick={() => {
            setShowLabelForm(true);
            setSelectedLabel({} as any);
          }}
        >
          <PlusIcon className="w-4 h-4" />
          <span>Add label</span>
        </div>
      </div>
    );
  };

  const renderColorMode = () => {
    return (
      <RadioGroup className="pb-2 flex items-center space-x-1 gap-1">
        {assignedLabels.map((option) => (
          <Radio
            key={option.name}
            value={option}
            aria-label={option.name}
            title={option.name}
            className={`${option.color} relative -m-0.5 flex cursor-pointer items-center justify-center rounded-full p-0.5 ring-current focus:outline-hidden`}
          >
            <span
              title={option.name}
              aria-hidden="true"
              className="h-1.5 w-8 border border-black border-opacity-10 bg-current rounded-sm"
            ></span>
          </Radio>
        ))}
      </RadioGroup>
    );
  };

  if (colorMode) {
    return renderColorMode();
  }

  return (
    <DropdownLayout
      activator={activator}
      bodyClassName={"mt-2 min-w-[240px]"}
      usePortal={true}
      portalZIndex={9999}
    >
      <div className={"w-full p-2"}>
        {renderAssigned ? renderAssigned(ctx) : null}
        {renderLabelList()}
        {renderLabelForm()}
      </div>
    </DropdownLayout>
  );
};

export default LabelPicker;
